/**
 * refrigerant.ts — REFRIG-01 (Seth 2026-09-25: "Refrigerant tracking for installs.
 * Where they can select refrigerant type used and how much weight … easily
 * tracked on the job and inventory … Should only apply for installs when we are
 * installing something that would use refrigerant. Furnace only shouldn't need it.")
 *
 * One log (pegasus_refrigerant_log) holds every ounce:
 *   charge  — weighed into a new system on a job (job cost, stock at close, the
 *             unit's 360 record: total charge + line set)
 *   recover — pulled from an old unit into a recovery tank (the EPA 608
 *             disposal record: where, when, type, pounds, which tank)
 *   handoff — a tank's contents sent to a reclaimer / supply house
 *   adjust  — a stock count correction
 *
 * Which equipment asks: equipment_category.uses_refrigerant (Settings). On a job,
 * the charge goes on the system's outdoor / packaged unit (AC, heat pump,
 * mini-split, Magic-Pak, rooftop); a coil or air handler asks only when there is
 * no such unit on the job (a coil-only swap still opens the refrigerant lines).
 * Competitor frame: ServiceTitan has no refrigerant feature (a per-lb price-book
 * item + a custom form); RunaCall logs pounds against the unit + an EPA log —
 * we do that, prefilled from the data tag the crew already photographs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { JobEquipment } from "./jobs";
import type { RemovedUnit } from "./installField";

export const REFRIGERANTS = ["R-454B", "R-410A", "R-32", "R-22", "R-407C"] as const;

/** Outdoor / packaged units carry the system's charge; coils + air handlers only when alone. */
const PRIMARY = new Set(["ac", "heat-pump", "mini-split", "magic-pak", "rooftop"]);

export interface RefrigerantLog {
  id: string;
  kind: "charge" | "recover" | "handoff" | "adjust";
  job_id: string | null;
  dispatch_id: string | null;
  job_equipment_id: string | null;
  removed_equipment_id: string | null;
  tank_id: string | null;
  item_id: string | null;
  refrigerant: string;
  oz: number;
  empty: boolean;
  factory_oz: number | null;
  factory_lineset_ft: number | null;
  lineset_ft: number | null;
  total_oz: number | null;
  metering: "txv" | "piston" | null;
  subcool_actual: number | null;
  subcool_target: number | null;
  superheat_actual: number | null;
  superheat_target: number | null;
  vacuum_microns: number | null;
  leak_test_psi: number | null;
  brazed_by: string | null;
  handoff_to: string | null;
  note: string | null;
  cost_per_lb: number | null;
  created_by: string | null;
  created_by_empno: string | null;
  created_at: string;
  updated_at: string;
}

export interface RefrigerantTank {
  id: string;
  label: string;
  refrigerant: string | null;
  capacity_lb: number | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  contents_oz: number;
  last_handoff_at: string | null;
}

/** 135 → "8 lb 7 oz"; 15 → "15 oz"; 128 → "8 lb". */
export function ozText(oz: number | null | undefined): string {
  if (oz == null || !Number.isFinite(Number(oz))) return "—";
  const n = Math.round(Number(oz) * 10) / 10;
  const lb = Math.floor(n / 16);
  const rest = Math.round((n - lb * 16) * 10) / 10;
  if (lb === 0) return `${rest} oz`;
  if (rest === 0) return `${lb} lb`;
  return `${lb} lb ${rest} oz`;
}
export const splitOz = (oz: number | null | undefined) => {
  const n = Math.max(0, Math.round(Number(oz ?? 0) * 10) / 10);
  const lb = Math.floor(n / 16);
  return { lb, oz: Math.round((n - lb * 16) * 10) / 10 };
};

/** Line set past what the factory charge covers × ounces per foot. */
export function suggestedAddOz(linesetFt: number | null | undefined, factoryFt: number | null | undefined, ozPerFt: number): number | null {
  if (!linesetFt || !factoryFt || !ozPerFt) return null;
  const extra = Number(linesetFt) - Number(factoryFt);
  if (extra <= 0) return 0;
  return Math.round(extra * ozPerFt * 10) / 10;
}

/** "R-454B" → "R454B" (the catalog's part number). */
const partKey = (r: string | null | undefined) => (r ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** Equipment category keys whose units hold refrigerant (Settings → Equipment Categories). */
export function useRefrigerantKeys() {
  return useQuery({
    queryKey: ["refrigerant_keys"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase.from("equipment_category").select("key").eq("uses_refrigerant", true).eq("active", true);
      if (error) throw error;
      return new Set(((data ?? []) as { key: string }[]).map((r) => r.key));
    },
  });
}

/** The new units that get a charge logged. */
export function chargeUnits(units: JobEquipment[], uses: Set<string> | undefined): JobEquipment[] {
  if (!uses) return [];
  const eq = units.filter((u) => u.kind !== "accessory" && u.kind !== "material" && u.equip_key && uses.has(u.equip_key));
  const primary = eq.filter((u) => PRIMARY.has(u.equip_key!));
  return primary.length ? primary : eq;
}

/** The old units that need a recovery record. */
export function recoverUnits(removed: RemovedUnit[], uses: Set<string> | undefined): RemovedUnit[] {
  if (!uses) return [];
  const eq = removed.filter((r) => r.equip_key && uses.has(r.equip_key));
  const primary = eq.filter((r) => PRIMARY.has(r.equip_key!));
  return primary.length ? primary : eq;
}

/** Does this old unit's kind hold refrigerant? (For the Old Unit sheet before it's saved.) */
export const REMOVED_KIND_KEY: Record<string, string> = {
  "Air Conditioner": "ac", "Heat Pump": "heat-pump", Coil: "coil", "Air Handler": "air-handler",
  Furnace: "furnace", Boiler: "boiler", "Water Heater": "water-heater",
};

export function useJobRefrigerant(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["job_refrigerant", jobId ?? null],
    enabled: !!jobId,
    queryFn: async (): Promise<RefrigerantLog[]> => {
      const { data, error } = await supabase.from("pegasus_refrigerant_log").select("*")
        .eq("job_id", jobId!).order("created_at").order("id");
      if (error) throw error;
      return (data ?? []) as RefrigerantLog[];
    },
  });
}

/** The refrigerant items in the catalog (for stock + cost). */
export function useRefrigerantItems() {
  return useQuery({
    queryKey: ["refrigerant_items"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from("item")
        .select("id, part_number, description, qty_in_stock, average_cost, last_purchase, units")
        .eq("category", "Refrigerant").or("archived.is.null,archived.eq.false");
      if (error) throw error;
      return (data ?? []) as { id: string; part_number: string | null; description: string | null; qty_in_stock: number | null; average_cost: number | null; last_purchase: number | null; units: string | null }[];
    },
  });
}

async function itemFor(refrigerant: string): Promise<{ id: string; cost: number | null } | null> {
  const { data } = await supabase.from("item").select("id, part_number, average_cost, last_purchase").eq("category", "Refrigerant");
  const hit = ((data ?? []) as { id: string; part_number: string | null; average_cost: number | null; last_purchase: number | null }[])
    .find((i) => partKey(i.part_number) === partKey(refrigerant));
  return hit ? { id: hit.id, cost: hit.average_cost ?? hit.last_purchase ?? null } : null;
}

async function whoAmI(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email ?? null;
}

function useInvalidateRefrigerant() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["job_refrigerant"] });
    void qc.invalidateQueries({ queryKey: ["refrigerant_tanks"] });
    void qc.invalidateQueries({ queryKey: ["refrigerant_report"] });
    void qc.invalidateQueries({ queryKey: ["jobs"] });
  };
}

export interface ChargeInput {
  jobId: string;
  dispatchId: string | null;
  unit: JobEquipment;
  refrigerant: string;
  factoryOz: number | null;
  factoryLinesetFt: number | null;
  linesetFt: number | null;
  addedOz: number;
  metering: "txv" | "piston" | null;
  subcoolActual: number | null;
  subcoolTarget: number | null;
  superheatActual: number | null;
  superheatTarget: number | null;
  vacuumMicrons: number | null;
  leakTestPsi: number | null;
  brazedBy: string | null;
  note: string | null;
  empno: string | null;
}

/** Save (or update) the charge on one new system. */
export function useSaveCharge() {
  const inval = useInvalidateRefrigerant();
  return useMutation({
    mutationFn: async (v: ChargeInput) => {
      const item = await itemFor(v.refrigerant);
      const email = await whoAmI();
      const total = v.factoryOz != null ? Math.round((Number(v.factoryOz) + Number(v.addedOz || 0)) * 10) / 10 : null;
      const row = {
        kind: "charge" as const,
        job_id: v.jobId, dispatch_id: v.dispatchId, job_equipment_id: v.unit.id,
        item_id: item?.id ?? null, cost_per_lb: item?.cost ?? null,
        refrigerant: v.refrigerant, oz: Math.max(0, Number(v.addedOz || 0)),
        factory_oz: v.factoryOz, factory_lineset_ft: v.factoryLinesetFt, lineset_ft: v.linesetFt, total_oz: total,
        metering: v.metering,
        subcool_actual: v.subcoolActual, subcool_target: v.subcoolTarget,
        superheat_actual: v.superheatActual, superheat_target: v.superheatTarget,
        vacuum_microns: v.vacuumMicrons, leak_test_psi: v.leakTestPsi,
        brazed_by: (v.brazedBy ?? "").trim() || null, note: (v.note ?? "").trim() || null,
        updated_at: new Date().toISOString(),
      };
      const { data: existing } = await supabase.from("pegasus_refrigerant_log").select("id")
        .eq("job_equipment_id", v.unit.id).eq("kind", "charge").maybeSingle();
      if (existing) {
        const { error } = await supabase.from("pegasus_refrigerant_log").update(row).eq("id", (existing as { id: string }).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_refrigerant_log").insert({ ...row, created_by: email, created_by_empno: v.empno });
        if (error) throw error;
      }
      // The unit's own facts (from the tag / the crew) ride on the unit too.
      const patch: Record<string, unknown> = {};
      if (v.refrigerant && v.refrigerant !== v.unit.refrigerant) patch.refrigerant = v.refrigerant;
      if (v.factoryOz != null && v.factoryOz !== v.unit.factory_charge_oz) patch.factory_charge_oz = v.factoryOz;
      if (v.factoryLinesetFt != null && v.factoryLinesetFt !== v.unit.factory_lineset_ft) patch.factory_lineset_ft = v.factoryLinesetFt;
      if (Object.keys(patch).length) await supabase.from("pegasus_job_equipment").update(patch).eq("id", v.unit.id);
    },
    onSuccess: () => {
      inval();
    },
  });
}

export interface RecoveryInput {
  jobId: string;
  dispatchId: string | null;
  removedId: string;
  refrigerant: string;
  oz: number;
  empty: boolean;
  tankId: string | null;
  nameplateOz: number | null;
  note: string | null;
  empno: string | null;
}

/** Save (or update) what came out of one old unit — the EPA disposal record. */
export function useSaveRecovery() {
  const inval = useInvalidateRefrigerant();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: RecoveryInput) => {
      const email = await whoAmI();
      const row = {
        kind: "recover" as const,
        job_id: v.jobId, dispatch_id: v.dispatchId, removed_equipment_id: v.removedId,
        refrigerant: v.refrigerant, oz: v.empty ? 0 : Math.max(0, Number(v.oz || 0)), empty: v.empty,
        tank_id: v.empty ? null : v.tankId,
        note: (v.note ?? "").trim() || null,
        updated_at: new Date().toISOString(),
      };
      const { data: existing } = await supabase.from("pegasus_refrigerant_log").select("id")
        .eq("removed_equipment_id", v.removedId).eq("kind", "recover").maybeSingle();
      if (existing) {
        const { error } = await supabase.from("pegasus_refrigerant_log").update(row).eq("id", (existing as { id: string }).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_refrigerant_log").insert({ ...row, created_by: email, created_by_empno: v.empno });
        if (error) throw error;
      }
      const patch: Record<string, unknown> = { refrigerant: v.refrigerant };
      if (v.nameplateOz != null) patch.nameplate_charge_oz = v.nameplateOz;
      await supabase.from("pegasus_job_removed_equipment").update(patch).eq("id", v.removedId);
    },
    onSuccess: () => {
      inval();
      void qc.invalidateQueries({ queryKey: ["job_removed_equipment"] });
    },
  });
}

export function useRefrigerantTanks() {
  return useQuery({
    queryKey: ["refrigerant_tanks"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<RefrigerantTank[]> => {
      const { data, error } = await supabase.from("v_refrigerant_tank").select("*").eq("active", true).order("label");
      if (error) throw error;
      return ((data ?? []) as RefrigerantTank[]).map((t) => ({ ...t, contents_oz: Number(t.contents_oz ?? 0) }));
    },
  });
}

export function useAddTank() {
  const inval = useInvalidateRefrigerant();
  return useMutation({
    mutationFn: async (v: { label: string; refrigerant: string | null; capacity_lb?: number | null }): Promise<string> => {
      const { data, error } = await supabase.from("pegasus_refrigerant_tank")
        .insert({ label: v.label.trim(), refrigerant: v.refrigerant, capacity_lb: v.capacity_lb ?? null, created_by: await whoAmI() })
        .select("id").single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: inval,
  });
}

/** A tank's contents sent out (reclaimer / buyback / supply house). */
export function useTankHandoff() {
  const inval = useInvalidateRefrigerant();
  return useMutation({
    mutationFn: async (v: { tank: RefrigerantTank; oz: number; to: string; note?: string | null }) => {
      const { error } = await supabase.from("pegasus_refrigerant_log").insert({
        kind: "handoff", tank_id: v.tank.id, refrigerant: v.tank.refrigerant ?? "Mixed",
        oz: Math.max(0, Number(v.oz || 0)), handoff_to: v.to.trim(), note: (v.note ?? "").trim() || null,
        created_by: await whoAmI(),
      });
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/** Set a refrigerant's stock to a counted number of pounds (a weigh-in). */
export function useCountRefrigerant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { itemId: string; refrigerant: string; lb: number; was: number | null }) => {
      const email = await whoAmI();
      const { error } = await supabase.from("item").update({ qty_in_stock: v.lb, updated_at: new Date().toISOString() }).eq("id", v.itemId);
      if (error) throw error;
      await supabase.from("pegasus_refrigerant_log").insert({
        kind: "adjust", item_id: v.itemId, refrigerant: v.refrigerant,
        oz: Math.round((v.lb - (v.was ?? 0)) * 16 * 10) / 10,
        note: v.was == null ? `Starting count: ${v.lb} lb` : `Counted ${v.lb} lb (was ${v.was} lb)`,
        created_by: email,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["refrigerant_items"] });
      void qc.invalidateQueries({ queryKey: ["refrigerant_report"] });
    },
  });
}

/** What the refrigerant charged on a job cost us — the pounds ADDED (the factory
 *  charge came with the unit). Cost per lb is snapshotted when the charge is
 *  logged; the catalog's average cost fills in when it wasn't. Same math as
 *  v_job_costing.refrigerant. */
export function refrigerantCost(
  logs: RefrigerantLog[],
  items?: { id: string; average_cost: number | null; last_purchase: number | null }[],
): number {
  const byId = new Map((items ?? []).map((i) => [i.id, i]));
  return logs.filter((l) => l.kind === "charge").reduce((s, l) => {
    const it = l.item_id ? byId.get(l.item_id) : undefined;
    const per = Number(l.cost_per_lb ?? it?.average_cost ?? it?.last_purchase ?? 0);
    return s + (Number(l.oz || 0) / 16) * per;
  }, 0);
}

/** What the office and the Ready to Finish card need to know about a job's refrigerant. */
export function refrigerantStatus(
  units: JobEquipment[], removed: RemovedUnit[], logs: RefrigerantLog[], uses: Set<string> | undefined,
) {
  const need = chargeUnits(units, uses);
  const charges = logs.filter((l) => l.kind === "charge");
  const chargedIds = new Set(charges.map((l) => l.job_equipment_id));
  const outs = recoverUnits(removed, uses);
  const recovers = logs.filter((l) => l.kind === "recover");
  const recoveredIds = new Set(recovers.map((l) => l.removed_equipment_id));
  const chargeMissing = need.filter((u) => !chargedIds.has(u.id));
  const recoverMissing = outs.filter((r) => !recoveredIds.has(r.id));
  const addedOz = charges.reduce((s, l) => s + Number(l.oz || 0), 0);
  const recoveredOz = recovers.reduce((s, l) => s + Number(l.oz || 0), 0);
  return {
    applies: need.length > 0 || outs.length > 0 || charges.length > 0 || recovers.length > 0,
    need, outs, charges, recovers, chargeMissing, recoverMissing,
    done: chargeMissing.length === 0 && recoverMissing.length === 0,
    addedOz, recoveredOz,
  };
}
